import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Users, X } from "lucide-react";
import { useShipperNetworks } from "@/hooks/useShipperNetworks";
import BrokerVisualPickerSheet, { type PickerBroker, type PickerNetwork } from "./BrokerVisualPickerSheet";

export interface NetworkBrokerSelection {
  networkId: string | null;
  targetBrokerUserIds: string[] | null;
  isValid: boolean;
  selectedCount: number;
  totalCount: number;
}

interface BrokerRow {
  user_id: string;
  company_id: string | null;
  display_name: string;
  secondary_name?: string;
  network_ids: string[];
}

const ALL_NETWORKS = "__all__";

interface Props {
  userId: string | undefined;
  value: NetworkBrokerSelection;
  onChange: (v: NetworkBrokerSelection) => void;
  /** Optional one-time prefill (e.g. ATLAS Broker Match → "Post to Top Brokers"). */
  preselectNetworkId?: string | null;
  preselectBrokerUserIds?: string[] | null;
}

export default function NetworkBrokerSelector({
  userId,
  value,
  onChange,
  preselectNetworkId,
  preselectBrokerUserIds,
}: Props) {
  const { networks, loading: networksLoading } = useShipperNetworks(userId);
  const [scopeId, setScopeId] = useState<string>("");
  const [brokers, setBrokers] = useState<BrokerRow[]>([]);
  const [loadingBrokers, setLoadingBrokers] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pickerOpen, setPickerOpen] = useState(false);
  const prefillAppliedRef = useRef(false);

  const defaultNetwork = useMemo(() => networks.find((n) => n.is_default) ?? networks[0] ?? null, [networks]);

  useEffect(() => {
    if (!networks.length || scopeId) return;
    const wanted = preselectNetworkId && networks.some((n) => n.id === preselectNetworkId)
      ? preselectNetworkId
      : defaultNetwork?.id ?? "";
    setScopeId(wanted);
  }, [networks, scopeId, defaultNetwork, preselectNetworkId]);


  useEffect(() => {
    if (!scopeId || !userId) { setBrokers([]); return; }
    let cancelled = false;
    (async () => {
      setLoadingBrokers(true);
      const networkIds = scopeId === ALL_NETWORKS ? networks.map((n) => n.id) : [scopeId];
      if (!networkIds.length) {
        if (!cancelled) { setBrokers([]); setLoadingBrokers(false); }
        return;
      }
      const { data: memberships } = await supabase
        .from("shipper_network_brokers" as any)
        .select("network_id, broker_user_id, broker_company_id")
        .in("network_id", networkIds)
        .eq("status", "accepted")
        .is("removed_at", null);

      const rows = ((memberships as any[]) || []).filter((r) => r.broker_user_id);
      const byUser = new Map<string, BrokerRow>();
      for (const r of rows) {
        const ex = byUser.get(r.broker_user_id);
        if (ex) {
          if (!ex.network_ids.includes(r.network_id)) ex.network_ids.push(r.network_id);
        } else {
          byUser.set(r.broker_user_id, {
            user_id: r.broker_user_id,
            company_id: r.broker_company_id ?? null,
            display_name: "",
            network_ids: [r.network_id],
          });
        }
      }
      const userIds = Array.from(byUser.keys());
      const companyIds = Array.from(new Set(
        Array.from(byUser.values()).map((b) => b.company_id).filter((x): x is string => !!x)
      ));
      if (userIds.length) {
        const { data: profs } = await (supabase as any)
          .from("profiles").select("id, first_name, last_name, email").in("id", userIds);
        for (const p of (profs as any[]) || []) {
          const b = byUser.get(p.id);
          if (!b) continue;
          const name = [p.first_name, p.last_name].filter(Boolean).join(" ").trim();
          b.secondary_name = name || p.email || undefined;
        }
      }
      const companyMap = new Map<string, string>();
      if (companyIds.length) {
        const { data: cs } = await supabase.from("companies").select("id, name").in("id", companyIds);
        for (const c of (cs as any[]) || []) companyMap.set(c.id, c.name);
      }
      const list = Array.from(byUser.values()).map((b) => ({
        ...b,
        display_name: String((b.company_id && companyMap.get(b.company_id)) || b.secondary_name || "Broker"),
      }));
      list.sort((a, b) => String(a.display_name).localeCompare(String(b.display_name)));

      if (!cancelled) {
        setBrokers(list);
        // One-time prefill from ATLAS Broker Match; otherwise default to "all".
        const wanted = (preselectBrokerUserIds || []).filter((id) => list.some((b) => b.user_id === id));
        if (!prefillAppliedRef.current && wanted.length) {
          prefillAppliedRef.current = true;
          setSelected(new Set(wanted));
        } else {
          setSelected(new Set(list.map((b) => b.user_id))); // default "all"
        }
        setLoadingBrokers(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeId, userId, networks]);


  useEffect(() => {
    if (!scopeId) {
      onChange({ networkId: null, targetBrokerUserIds: null, isValid: false, selectedCount: 0, totalCount: 0 });
      return;
    }
    const total = brokers.length;
    const allSelected = total > 0 && selected.size === total;
    const isValid = selected.size > 0;
    let networkId: string | null;
    let targets: string[] | null;
    if (scopeId === ALL_NETWORKS) {
      networkId = defaultNetwork?.id ?? null;
      targets = Array.from(selected);
    } else {
      networkId = scopeId;
      targets = allSelected ? null : Array.from(selected);
    }
    onChange({
      networkId,
      targetBrokerUserIds: targets,
      isValid: isValid && !!networkId,
      selectedCount: selected.size,
      totalCount: total,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeId, selected, brokers, defaultNetwork]);

  if (networksLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading your networks…
      </div>
    );
  }
  if (!networks.length) {
    return (
      <div className="text-xs text-amber-600 py-2">
        No private network yet — set one up in My Network first.
      </div>
    );
  }

  const total = brokers.length;
  const allSelected = total > 0 && selected.size === total;
  const scopeLabel =
    scopeId === ALL_NETWORKS
      ? "My Entire Private Network"
      : networks.find((n) => n.id === scopeId)?.name ?? "network";

  const pickerNetworks: PickerNetwork[] = scopeId === ALL_NETWORKS
    ? networks.map((n) => ({ id: n.id, name: n.name }))
    : [];
  const pickerBrokers: PickerBroker[] = brokers.map((b) => ({
    user_id: b.user_id,
    display_name: b.display_name,
    secondary_name: b.secondary_name,
    network_ids: b.network_ids,
  }));

  const selectedBrokers = brokers.filter((b) => selected.has(b.user_id));

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label className="text-sm font-medium">Send to</Label>
        <Select value={scopeId} onValueChange={setScopeId}>
          <SelectTrigger><SelectValue placeholder="Select network" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_NETWORKS}>My Entire Private Network</SelectItem>
            {networks.map((n) => (
              <SelectItem key={n.id} value={n.id}>
                {n.name}{n.is_default ? " (default)" : ""}
                {typeof n.member_count === "number" ? ` · ${n.member_count}` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border border-border bg-card px-3 py-2.5">
        {loadingBrokers ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading brokers…
          </div>
        ) : total === 0 ? (
          <span className="text-xs text-muted-foreground">No brokers in this scope yet.</span>
        ) : allSelected ? (
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm min-w-0">
              <Users className="h-4 w-4 text-brand-teal shrink-0" />
              <span className="truncate">
                All <span className="font-medium">{total}</span> broker{total === 1 ? "" : "s"} in {scopeLabel} will see this
              </span>
            </div>
            <Button type="button" variant="outline" size="sm" className="h-8 text-xs shrink-0" onClick={() => setPickerOpen(true)}>
              Customize Recipients
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs text-muted-foreground">
                Sending to <span className="font-medium text-foreground">{selected.size}</span> of {total} broker{total === 1 ? "" : "s"}
              </div>
              <div className="flex items-center gap-1">
                <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setSelected(new Set(brokers.map((b) => b.user_id)))}>
                  Send to all
                </Button>
                <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => setPickerOpen(true)}>
                  Edit
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {selectedBrokers.slice(0, 12).map((b) => (
                <Badge key={b.user_id} variant="secondary" className="gap-1 pr-1 max-w-[220px]">
                  <span className="truncate">{b.display_name}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${b.display_name}`}
                    onClick={() => setSelected((p) => { const n = new Set(p); n.delete(b.user_id); return n; })}
                    className="rounded-sm hover:bg-muted-foreground/20 p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              {selectedBrokers.length > 12 && (
                <Badge variant="outline" className="text-xs">+{selectedBrokers.length - 12} more</Badge>
              )}
            </div>
          </div>
        )}
      </div>

      {value.isValid === false && brokers.length > 0 && (
        <p className="text-xs text-destructive">Select at least one broker.</p>
      )}

      <BrokerVisualPickerSheet
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        title="Select Brokers"
        brokers={pickerBrokers}
        networks={pickerNetworks}
        initialSelectedIds={Array.from(selected)}
        allowAllAsNull
        applyLabel="Apply"
        emptyMessage="No brokers in this scope yet."
        onApply={(ids) => {
          if (ids === null) setSelected(new Set(brokers.map((b) => b.user_id)));
          else setSelected(new Set(ids));
        }}
      />
    </div>
  );
}
